/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createEntityLabelStore } from 'src/luis/projects/utterances/stores/entityLabelStore';
import { PredictionStore } from 'src/luis/projects/utterances/stores/predictionStore';
import { SuggestedUtteranceStore } from 'src/luis/projects/utterances/stores/suggestedUtteranceStore';
import { UtteranceStore } from 'src/luis/projects/utterances/stores/utteranceStore';
import { LuisId } from 'src/luis/types/Resource';
import { EntityLabelStore } from 'src/luis/types/stores/utterances/EntityLabelStore';

export type AddExampleSwagger = { ExampleId: number; UtteranceText: string };

export type AddExamplePayloadLabelSwagger = {
    entityName: string;
    startCharIndex: number;
    endCharIndex: number;
    role?: string;

    /**
     * Optional to support both flat and nested formats
     * @todo
     * remove '?' when flat format is not needed
     */
    children?: AddExamplePayloadLabelSwagger[];
};

export type AddExampleSwaggerPayload = {
    text: string;
    intentName: string;
    entityLabels: AddExamplePayloadLabelSwagger[];
};

export type BatchAddExampleSwagger = {
    value: { ExampleId: number; UtteranceText: string };
    hasError: boolean;
    error?: {
        code: string;
        message: string;
    };
}[];

export type UtteranceSwagger = {
    id: number;
    text: string;
    intentId: string;
    tokenizedText: string[];
    entityLabels: LabelSwagger[] | null;
    createdDateTime: string;
    modifiedDateTime: string;
};

export type LabelSwagger = {
    id: string;
    entityName: string;
    phrase: string;
    entityType: number;
    role: string;
    startTokenIndex: number;
    endTokenIndex: number;
    endCharIndex?: number;
    startCharIndex?: number;
    roleId: string;
};

export type SuggestedUtteranceEntityPrediction = Omit<LabelSwagger, 'roleId' | 'id' | 'endCharIndex' | 'startCharIndex'> & {
    id: string;
    role: { id: string };
};

export type IntentPrediction = { id: string; name: string; score: number };

export type SuggestedUtteranceSwagger = {
    intentPredictions: IntentPrediction[];
    entityPredictions: SuggestedUtteranceEntityPrediction[];
    text: string;
    tokenizedText: string[];
};

export type PredictionSwagger = {
    entityPredictions: LabelSwagger[];
    intentPredictions: IntentPrediction[];
    text: string;
    tokenizedText: string[];
    patternPredictions: {
        pattern: string;
        intent: string;
        order: number;
    }[];
    alteredText: string;
    sentimentAnalysis: { label: string; score: number };
};

/**
 * Creates an utterance store item from the api received
 * utterance object.
 *
 * @param utterance The utterance object received from the api.
 * @returns An utterance store item.
 */
export const utteranceSwaggerToUtteranceStore = (utterance: UtteranceSwagger): UtteranceStore =>
    new UtteranceStore(utterance.id, {
        labeledIntentId: utterance.intentId,
        text: utterance.text,
        tokenizedText: utterance.tokenizedText,
        labeledEntities: utterance.entityLabels ? utterance.entityLabels.map(labelToLabelStore) : [],
        createdDate: new Date(utterance.createdDateTime),
        modifiedDate: new Date(utterance.modifiedDateTime)
    });

export const predictionSwaggerToPredictionStore = (prediction: PredictionSwagger) =>
    new PredictionStore({
        predictedEntities: prediction.entityPredictions ? prediction.entityPredictions.map(labelToLabelStore) : [],
        text: prediction.text,
        tokenizedText: prediction.tokenizedText,
        predictedIntents: prediction.intentPredictions
            ? prediction.intentPredictions.map(intent => ({ intentId: intent.id, score: intent.score }))
            : []
    });

const labelToLabelStore = (label: LabelSwagger) =>
    createEntityLabelStore(
        label.id,
        {
            startTokenIndex: label.startTokenIndex,
            endTokenIndex: label.endTokenIndex,
            endCharIndex: null,
            startCharIndex: null
        },
        label.roleId ? label.roleId : null
    );

const suggestedUtteranceEntityPredictionToLabelStore = (label: SuggestedUtteranceEntityPrediction) =>
    createEntityLabelStore(
        label.id,
        {
            startTokenIndex: label.startTokenIndex,
            endTokenIndex: label.endTokenIndex,
            endCharIndex: null,
            startCharIndex: null
        },
        label.role && label.role.id
    );

export const exampleSwaggerToUtteranceStore = (example: AddExampleSwagger, intentId: string, labeledEntities: EntityLabelStore[]) =>
    new UtteranceStore(example.ExampleId, {
        text: example.UtteranceText,
        tokenizedText: example.UtteranceText.split(' '),
        labeledEntities,
        labeledIntentId: intentId,
        createdDate: new Date(),
        modifiedDate: new Date()
    });

export const batchAddExampleToUtteranceStores = (examples: BatchAddExampleSwagger, intentId: LuisId) =>
    examples.map(({ value }) => exampleSwaggerToUtteranceStore(value, <string>intentId, []));

/**
 * Creates a suggested utterance store from the data received.
 *
 * @param appItem The data received from the backend.
 * @returns A suggested utterance store.
 */
export const suggestedUtteranceSwaggerToSuggestedUtteranceStore = (appItem: SuggestedUtteranceSwagger): SuggestedUtteranceStore =>
    new SuggestedUtteranceStore({
        predictedIntents: appItem.intentPredictions
            ? appItem.intentPredictions.map(intent => ({ intentId: intent.id, score: intent.score }))
            : [],
        predictedEntities: appItem.entityPredictions ? appItem.entityPredictions.map(suggestedUtteranceEntityPredictionToLabelStore) : [],
        text: appItem.text,
        tokenizedText: appItem.tokenizedText
    });
